import React, { useState, MouseEvent, useEffect, ReactElement } from 'react';
import {
  useFilters,
  useRowSelect,
  useSortBy,
  useTable,
  usePagination,
  useExpanded,
  useGroupBy,
  HeaderGroup,
  Column,
} from 'react-table';
import Layout from 'components/layout';
import PageContainer from 'components/page-container';
import { renderSortIcon } from 'components/deal-list/helpers';
import StyledInput from 'components/input/text';
import Pagination from 'components/deal-list/pagination';
import Table from 'components/table/table';
import styled from 'styled-components';
import MenuItem from 'components/menu-item';
import Menu from 'components/menu';
import { MenuItemText } from 'components/header/components';
import { ModalButton, ModalTextButton } from 'components/modal-provider/modal';
import history from 'lib/history';
import labels from 'constants/labels';
import ModalBasic from 'components/modal-provider/modal-basic';
import MarkerSearchBox from 'components/formula-entry/marker-search-box';
import { toast } from 'react-toastify';
import messages from 'constants/messages';
import { IBookmarkObjState } from 'store/bookmark/types';
import BookmarkViewIcon from 'assets/svg/BookmarkViewIcon';
import BookmarkActionsIcon from 'assets/svg/BookmarkActionsIcon';

interface ITableProps {
  columns: Column<object>[];
  data: IBookmarkObjState[];
  clearModalContent: () => void;
  setModalContent: (content: ReactElement) => void;
  deleteBookmark: (id: string) => void;
  renameBookmark: (bookmarkObj: { name: string; link: string }) => void;
  setBookmarkName: (bookmarkName: string, bookmarkLink: string, bookmarkModule: string) => void;
  setUserSearchInput: (userInput: string) => void;
  bookmarkName: string;
  bookmarkLink: string;
  searchInputValue: string;
  renameBookmarkSuccess: boolean;
}

interface IRowType {
  id: string;
  name: string;
  link: string;
  module: string;
}

const BookmarkHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 130px;
`;

const Title = styled.h2`
  font-weight: 600;
  font-size: 22px;
  margin: 0 12px;
`;

const BookmarkSearchContainer = styled.div`
  height: 70px;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
`;

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
`;

const ColumnHeaderLabel = styled.span``;

const NoBookmarksMessage = styled.td`
  text-align: center;
  padding: 32px !important;
`;

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
  input {
    width: 200px;
  }
`;

const BookmarkButton = styled.button`
  border: none;
  background-color: transparent;
`;

export default (props: ITableProps) => {
  const {
    columns,
    data,
    clearModalContent,
    setModalContent,
    deleteBookmark,
    renameBookmark,
    setBookmarkName,
    setUserSearchInput,
    searchInputValue,
    renameBookmarkSuccess,
    bookmarkName,
  } = props;

  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [userSelectedRow, setUserSelectedRow] = useState({
    id: '',
    name: '',
    link: '',
    module: '',
  });

  const toggleActionMenu = (event: MouseEvent<HTMLElement>, row: IRowType) => {
    setUserSelectedRow(row);
    const anchorValue = actionMenuAnchor ? null : event.currentTarget;
    setActionMenuAnchor(anchorValue);
  };

  const toggleMenu = (event: MouseEvent<HTMLElement>) => {
    const anchorValue = actionMenuAnchor ? null : event.currentTarget;
    setActionMenuAnchor(anchorValue);
  };

  useEffect(() => {
    if (renameBookmarkSuccess) {
      clearModalContent();
      toast.dismiss();
      toast.success(`${bookmarkName} ${messages.success.bookmarkUpdated}`, { autoClose: 3000 });
    }
  }, [renameBookmarkSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  function renderHeaderDivComponent() {
    return <div />;
  }

  function renderCellDivComponent(row: IRowType) {
    return (
      <div>
        <BookmarkButton onClick={e => toggleActionMenu(e, row)}>
          <BookmarkActionsIcon />
        </BookmarkButton>
      </div>
    );
  }

  const {
    state: { pageIndex },
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10, sortBy: [{ id: 'createdOn', desc: true }] },
    },
    useGroupBy,
    useExpanded,
    useFilters,
    useSortBy,
    useRowSelect,
    usePagination,

    hooks => {
      hooks.flatColumns.push((cols: string[]) => [
        ...cols,
        {
          id: 'selection',
          Header: () => renderHeaderDivComponent(),
          Cell: (row: { cell: { row: { original: IRowType } } }) => renderCellDivComponent(row.cell.row.original),
        },
      ]);
    }
  );

  function handleSearchValueChange(e: React.ChangeEvent<HTMLInputElement>) {
    setUserSearchInput(e.target.value);
  }

  function handleSearchValueClear() {
    setUserSearchInput('');
  }

  const handleDelete = (rowObj: { id: string; name: string }) => {
    deleteBookmark(rowObj.id);
    toast.success(`${rowObj.name} ${messages.success.bookmarkDeleted}`, { autoClose: 3000 });
    clearModalContent();
  };

  const handleSave = (rowObj: { name: string; link: string }) => {
    renameBookmark(rowObj);
  };

  const handleCancel = () => {
    clearModalContent();
    toast.dismiss();
  };

  const handleRenameModal = (event: MouseEvent<HTMLElement>, row: IRowType) => {
    toggleActionMenu(event, row);
    const modalContent = (
      <>
        <p>Enter bookmark name</p>
        <StyledInput
          width="386px"
          defaultValue={row.name}
          onBlur={e => {
            setBookmarkName(e.target.value, row.link, row.module);
          }}
        />
        <br />
      </>
    );
    const buttons = (
      <>
        <ModalTextButton onClick={handleCancel}>{labels.button.cancel}</ModalTextButton>
        <ModalButton onClick={() => handleSave(row)}>{labels.button.save}</ModalButton>
      </>
    );
    const modal = (
      <ModalBasic title="Rename bookmark" content={modalContent} onClose={clearModalContent} buttons={buttons} />
    );
    setModalContent(modal);
  };

  const handleDeleteModal = (event: MouseEvent<HTMLElement>, row: IRowType) => {
    toggleActionMenu(event, row);
    const modalContent = (
      <>
        <p>Are you sure you want to delete {row.name} bookmark?</p>
        <br />
      </>
    );
    const buttons = (
      <>
        <ModalTextButton onClick={clearModalContent}>{labels.button.cancel}</ModalTextButton>
        <ModalButton onClick={() => handleDelete(row)}>{labels.button.delete}</ModalButton>
      </>
    );
    const modal = (
      <ModalBasic title="Delete bookmark?" content={modalContent} onClose={clearModalContent} buttons={buttons} />
    );
    setModalContent(modal);
  };

  return (
    <Layout>
      <PageContainer>
        <BookmarkHeader>
          <BookmarkSearchContainer>
            <BookmarkViewIcon />
            <Title>My Bookmarks</Title>
            <Container>
              <MarkerSearchBox
                data-testid="tid-bookmark-search-box"
                value={searchInputValue}
                onChange={handleSearchValueChange}
                onClear={handleSearchValueClear}
                placeholder="Search bookmark"
              />
            </Container>
          </BookmarkSearchContainer>
        </BookmarkHeader>
        <Table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup: HeaderGroup<object>) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    <ColumnHeader>
                      <ColumnHeaderLabel>{column.render('Header')}</ColumnHeaderLabel>
                      {renderSortIcon(column)}
                    </ColumnHeader>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length ? (
              page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                    })}
                    <Menu
                      id="header-menu"
                      anchorEl={actionMenuAnchor}
                      keepMounted
                      open={Boolean(actionMenuAnchor)}
                      onClose={toggleMenu}
                    >
                      <MenuItem onClick={() => history.push(userSelectedRow.link)}>
                        <MenuItemText>View</MenuItemText>
                      </MenuItem>
                      <MenuItem onClick={e => handleRenameModal(e, userSelectedRow)}>
                        <MenuItemText>Rename</MenuItemText>
                      </MenuItem>
                      <MenuItem onClick={e => handleDeleteModal(e, userSelectedRow)}>
                        <MenuItemText>Delete</MenuItemText>
                      </MenuItem>
                    </Menu>
                  </tr>
                );
              })
            ) : (
              <tr>
                <NoBookmarksMessage colSpan={100}>{messages.warnings.noBookmarks}</NoBookmarksMessage>
              </tr>
            )}
          </tbody>
        </Table>
        <Pagination
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          gotoPage={gotoPage}
          nextPage={nextPage}
          pageCount={pageCount}
          pageIndex={pageIndex}
          previousPage={previousPage}
        />
      </PageContainer>
    </Layout>
  );
};
